import React__default, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useQueryChildComponents } from '../base/SystemComponent.js';
import { Button } from '../button/Button.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Icon, __Icon } from '../icon/Icon.js';
import { Box } from '../layout/Box.js';
import { Inline } from '../layout/Inline.js';
import { Stack } from '../layout/Stack.js';
import { CSSOpacityHex } from '../styles/cssGenerate.js';
import { Text } from '../text/Text.js';
import { Tooltip } from '../tooltip/Tooltip.js';

const ChipButton = styled(Button)`
  &[${Button.dataId}] {
    border: 1px solid transparent;
    line-height: 1.25;
    padding: 0;
    justify-content: flex-start;
    background: ${COLOR_PALETTE.transparent};
    height: 3rem;
    padding: 0.25rem;
    user-select: text;

    &[disabled] {
      border-color: transparent;
      color: ${COLOR_PALETTE.slateGrey100};
    }

    &[readonly] {
      pointer-events: none;
    }
  }
`;
const StyledBox = styled(Box)`
  border: 1px solid transparent;
  padding: 0;
  background: ${COLOR_PALETTE.transparent};
  border-radius: 2.5rem;
  height: 3rem;

  :hover,
  :focus-within {
    background: ${(props)=>props.$readOnly || props.$disabled ? "background: none" : `${COLOR_PALETTE.slateGrey100 + CSSOpacityHex(0.05)}`};
    border-color: transparent;
    cursor: ${(props)=>props.$readOnly || props.$disabled ? "default" : "pointer"};
  }

  :active {
    background: ${COLOR_PALETTE.slateGrey100 + CSSOpacityHex(0.1)};
  }

  /* show action buttons when hovered or focused (if the action button is hidden) */
  :not(:hover):not(:focus-within) [data-hpy-web-chip-actions] > * {
    display: none;
  }
`;
const ChipIconWrapper = styled(Box)`
  height: 2.25rem;
  width: 2.25rem;
  color: ${COLOR_PALETTE.slateGrey60};
  border: 1px solid ${COLOR_PALETTE.slateGrey100}${CSSOpacityHex(0.1)};
  border-radius: 50%;
  padding: calc(0.25rem - 1px);

  & [${Icon.dataId}] {
    font-size: 1.75rem;
  }
`;
const Title = styled(Text)`
  /* Prevent selection of the title label as it conflicts with selecting the elements value text */
  user-select: none;
  color: ${COLOR_PALETTE.slateGrey40};
`;
const SubLabel = styled(Text)`
  ${(props)=>props.$highlight && css`
      color: ${COLOR_PALETTE.red100};
    `};
`;
/**
 * Generic 'info' chip component - shows a large, clickable chip
 * for displaying editable info
 *
 * @example
 * // With icon, label text
 * <Chip label="Check Light Bulbs" sublabel="Checked!" icon='cr-light-bulb' />
 *
 * // with avatar
 * <Chip label="Check Light Bulbs" sublabel="Herkle Derkle" >
 *  <Avatar>HD</Avatar>
 * </ListRow>
 *
 */ const Chip = // eslint-disable-next-line react/display-name
forwardRef((props, ref)=>{
    return React__default.createElement(ChipWithRef, {
        ...props,
        forwardedRef: ref
    });
});
Chip.displayName = "Chip";
Chip.dataId = "data-hpy-web-chip";
const ChipWithRef = ({ label , sublabel , icon , isHighlighted , highlight , children , forwardedRef , readOnly =false , disabled =false , testId ="X" , ...props })=>{
    const [buttonChildren, otherChildren] = useQueryChildComponents(children, [
        Tooltip,
        ButtonIcon
    ]);
    return React__default.createElement(StyledBox, {
        width: "fill",
        "$readOnly": readOnly,
        "$disabled": disabled
    }, React__default.createElement(ChipButton, {
        "data-hpy-web-chip": true,
        ...props,
        ref: forwardedRef,
        testId: "Chip-" + testId,
        width: "fill",
        disabled: readOnly || disabled
    }, React__default.createElement(Inline, {
        gap: 0.5,
        width: "fill",
        alignY: "center",
        wrap: false
    }, !!icon && React__default.createElement(ChipIconWrapper, null, React__default.createElement(__Icon, {
        icon: icon
    })), !icon && otherChildren, React__default.createElement(Box, {
        width: "fill"
    }, React__default.createElement(Stack, {
        align: "left"
    }, React__default.createElement(Title, {
        weight: "bold",
        size: "small"
    }, label), React__default.createElement(SubLabel, {
        weight: "bold",
        size: "medium",
        truncate: true,
        "$highlight": isHighlighted || highlight
    }, sublabel || "-"))))), React__default.createElement(Box, {
        padding: [
            0.5,
            0.25
        ]
    }, React__default.createElement(Inline, {
        "data-hpy-web-chip-actions": true,
        alignY: "center",
        align: "right",
        width: "content",
        wrap: false,
        gap: 0.25
    }, buttonChildren)));
};
ChipWithRef.displayName = "Chip";
ChipWithRef.dataId = "data-hpy-web-chip";

export { Chip };
